import React from "react";
import { Modal, Typography, Divider, Row, Col, Tag } from "antd";
import {
    HomeOutlined,
    UserOutlined,
    BankOutlined,
    ProjectOutlined,
    SolutionOutlined,
    ProfileOutlined,
    EuroOutlined,
    ContainerOutlined,
    AppstoreOutlined,
    RiseOutlined,
    SwapOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Title, Paragraph, Text } = Typography;

interface HelpModalProps {
    visible: boolean;
    onClose: () => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ visible, onClose }) => {
    const { t } = useTranslation();

    return (
        <Modal
            title={
                <div style={{ textAlign: 'center' }}>
                    <Title level={3} style={{ margin: 0, color: '#006778' }}>
                        📖 {t('component:helpModal.title')}
                    </Title>
                </div>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={900}
            style={{ top: 20 }}
            bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', padding: '24px' }}
        >
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Paragraph style={{ fontSize: '16px', color: '#666' }}>
                    {t('component:helpModal.description')}
                </Paragraph>
            </div>

            <Divider orientation="left">
                <Title level={4} style={{ color: '#006778' }}>
                    🏠 {t('component:helpModal.mainFunctionalitiesTitle')}
                </Title>
            </Divider>

            {/* Inicio */}
            <div style={{ marginBottom: 24 }}>
                <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col>
                        <HomeOutlined style={{ fontSize: '20px', color: '#006778' }} />
                    </Col>
                    <Col>
                        <Title level={5} style={{ margin: 0 }}>{t('component:helpModal.dashboard.title')}</Title>
                    </Col>
                </Row>
                <Paragraph style={{ marginLeft: 32, marginBottom: 8 }}>
                    {t('component:helpModal.dashboard.description')}
                </Paragraph>
                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.dashboard.mainFeatures')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.dashboard.features.realTimeStats')}</Text></li>
                        <li><Text>{t('component:helpModal.dashboard.features.temporalGraphics')}</Text></li>
                        <li><Text>{t('component:helpModal.dashboard.features.quickAccess')}</Text></li>
                        <li><Text>{t('component:helpModal.dashboard.features.teamActivity')}</Text></li>
                    </ul>
                </div>
                <div style={{ marginLeft: 32, marginTop: 12 }}>
                    <Tag color="blue">{t('component:helpModal.dashboard.tags.statisticalSummary')}</Tag>
                    <Tag color="green">{t('component:helpModal.dashboard.tags.quickAccess')}</Tag>
                    <Tag color="orange">{t('component:helpModal.dashboard.tags.impactMetrics')}</Tag>
                </div>
            </div>

            {/* Usuarios */}
            <div style={{ marginBottom: 24 }}>
                <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col>
                        <UserOutlined style={{ fontSize: '20px', color: '#006778' }} />
                    </Col>
                    <Col>
                        <Title level={5} style={{ margin: 0 }}>{t('component:helpModal.users.title')}</Title>
                    </Col>
                </Row>
                <Paragraph style={{ marginLeft: 32, marginBottom: 8 }}>
                    {t('component:helpModal.users.description')}
                </Paragraph>
                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.users.availableInfo')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.users.infoList.basicData')}</Text></li>
                        <li><Text>{t('component:helpModal.users.infoList.contactInfo')}</Text></li>
                        <li><Text>{t('component:helpModal.users.infoList.activityStatus')}</Text></li>
                        <li><Text>{t('component:helpModal.users.infoList.lastAccess')}</Text></li>
                        <li><Text>{t('component:helpModal.users.infoList.department')}</Text></li>
                    </ul>
                </div>
                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.users.queryFunctionalities')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.users.functionalitiesList.completeList')}</Text></li>
                        <li><Text>{t('component:helpModal.users.functionalitiesList.quickSearch')}</Text></li>
                        <li><Text>{t('component:helpModal.users.functionalitiesList.filters')}</Text></li>
                        <li><Text>{t('component:helpModal.users.functionalitiesList.contactView')}</Text></li>
                    </ul>
                </div>
                <div style={{ marginLeft: 32, marginTop: 12 }}>
                    <Tag color="blue">{t('component:helpModal.users.tags.staffList')}</Tag>
                    <Tag color="green">{t('component:helpModal.users.tags.contactData')}</Tag>
                    <Tag color="orange">{t('component:helpModal.users.tags.searchFilters')}</Tag>
                    <Tag color="purple">{t('component:helpModal.users.tags.userStatus')}</Tag>
                </div>
            </div>

            {/* Entidad */}
            <div style={{ marginBottom: 24 }}>
                <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col>
                        <BankOutlined style={{ fontSize: '20px', color: '#006778' }} />
                    </Col>
                    <Col>
                        <Title level={5} style={{ margin: 0 }}>{t('component:helpModal.entity.title')}</Title>
                    </Col>
                </Row>
                <Paragraph style={{ marginLeft: 32, marginBottom: 8 }}>
                    {t('component:helpModal.entity.description')}
                </Paragraph>

                <div style={{ marginLeft: 48, marginTop: 16 }}>
                    <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                        <Col><HomeOutlined style={{ color: '#52c41a' }} /></Col>
                        <Col><Text strong>{t('component:helpModal.entity.generalData.title')}</Text></Col>
                    </Row>
                    <div style={{ marginLeft: 32, marginBottom: 12 }}>
                        <Text>{t('component:helpModal.entity.generalData.description')}</Text>
                        <ul style={{ marginTop: 8 }}>
                            <li><Text>{t('component:helpModal.entity.generalData.items.fiscalData')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.generalData.items.contactInfo')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.generalData.items.certifications')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.generalData.items.operativeParams')}</Text></li>
                        </ul>
                    </div>

                    <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                        <Col><UserOutlined style={{ color: '#52c41a' }} /></Col>
                        <Col><Text strong>{t('component:helpModal.entity.humanResources.title')}</Text></Col>
                    </Row>
                    <div style={{ marginLeft: 32, marginBottom: 12 }}>
                        <Text>{t('component:helpModal.entity.humanResources.description')}</Text>
                        <ul style={{ marginTop: 8 }}>
                            <li><Text>{t('component:helpModal.entity.humanResources.items.personalFiles')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.humanResources.items.contracts')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.humanResources.items.payroll')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.humanResources.items.dedication')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.humanResources.items.training')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.humanResources.items.evaluation')}</Text></li>
                        </ul>
                    </div>

                    <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                        <Col><ContainerOutlined style={{ color: '#52c41a' }} /></Col>
                        <Col><Text strong>{t('component:helpModal.entity.inventory.title')}</Text></Col>
                    </Row>
                    <div style={{ marginLeft: 32, marginBottom: 12 }}>
                        <Text>{t('component:helpModal.entity.inventory.description')}</Text>
                        <ul style={{ marginTop: 8 }}>
                            <li><Text>{t('component:helpModal.entity.inventory.items.catalog')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.inventory.items.purchases')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.inventory.items.assignment')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.inventory.items.maintenance')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.inventory.items.valuation')}</Text></li>
                        </ul>
                    </div>

                    <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                        <Col><EuroOutlined style={{ color: '#52c41a' }} /></Col>
                        <Col><Text strong>{t('component:helpModal.entity.economicManagement.title')}</Text></Col>
                    </Row>
                    <div style={{ marginLeft: 48, marginBottom: 12 }}>
                        <Text strong style={{ color: '#1890ff' }}>• {t('component:helpModal.entity.economicManagement.income.title')}</Text>
                        <ul style={{ marginTop: 8, marginLeft: 16 }}>
                            <li><Text>{t('component:helpModal.entity.economicManagement.income.items.subsidies')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.economicManagement.income.items.donations')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.economicManagement.income.items.services')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.economicManagement.income.items.justifications')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.economicManagement.income.items.pendingCollection')}</Text></li>
                        </ul>

                        <Text strong style={{ color: '#1890ff' }}>• {t('component:helpModal.entity.economicManagement.expenses.title')}</Text>
                        <ul style={{ marginTop: 8, marginLeft: 16 }}>
                            <li><Text>{t('component:helpModal.entity.economicManagement.expenses.items.invoices')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.economicManagement.expenses.items.categorization')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.economicManagement.expenses.items.payments')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.economicManagement.expenses.items.reports')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.economicManagement.expenses.items.integration')}</Text></li>
                        </ul>
                    </div>

                    <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                        <Col><AppstoreOutlined style={{ color: '#52c41a' }} /></Col>
                        <Col><Text strong>{t('component:helpModal.entity.commercialRelations.title')}</Text></Col>
                    </Row>
                    <div style={{ marginLeft: 48, marginBottom: 12 }}>
                        <Text strong style={{ color: '#1890ff' }}>• {t('component:helpModal.entity.commercialRelations.creditors.title')}</Text>
                        <ul style={{ marginTop: 8, marginLeft: 16 }}>
                            <li><Text>{t('component:helpModal.entity.commercialRelations.creditors.items.financial')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.commercialRelations.creditors.items.debts')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.commercialRelations.creditors.items.history')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.commercialRelations.creditors.items.alerts')}</Text></li>
                        </ul>

                        <Text strong style={{ color: '#1890ff' }}>• {t('component:helpModal.entity.commercialRelations.suppliers.title')}</Text>
                        <ul style={{ marginTop: 8, marginLeft: 16 }}>
                            <li><Text>{t('component:helpModal.entity.commercialRelations.suppliers.items.completeData')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.commercialRelations.suppliers.items.servicesHistory')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.commercialRelations.suppliers.items.commercialTerms')}</Text></li>
                            <li><Text>{t('component:helpModal.entity.commercialRelations.suppliers.items.contracts')}</Text></li>
                        </ul>
                    </div>
                </div>
                <div style={{ marginLeft: 32, marginTop: 16 }}>
                    <Tag color="blue">{t('component:helpModal.entity.tags.administration')}</Tag>
                    <Tag color="green">{t('component:helpModal.entity.tags.humanResources')}</Tag>
                    <Tag color="orange">{t('component:helpModal.entity.tags.inventory')}</Tag>
                    <Tag color="purple">{t('component:helpModal.entity.tags.finances')}</Tag>
                    <Tag color="cyan">{t('component:helpModal.entity.tags.suppliers')}</Tag>
                </div>
            </div>

            {/* Proyectos */}
            <div style={{ marginBottom: 24 }}>
                <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col>
                        <ProjectOutlined style={{ fontSize: '20px', color: '#006778' }} />
                    </Col>
                    <Col>
                        <Title level={5} style={{ margin: 0 }}>{t('component:helpModal.projects.title')}</Title>
                    </Col>
                </Row>
                <Paragraph style={{ marginLeft: 32, marginBottom: 8 }}>
                    {t('component:helpModal.projects.description')}
                </Paragraph>
                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.projects.planningFunctionalities')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.projects.planningList.objectives')}</Text></li>
                        <li><Text>{t('component:helpModal.projects.planningList.temporal')}</Text></li>
                        <li><Text>{t('component:helpModal.projects.planningList.humanResources')}</Text></li>
                        <li><Text>{t('component:helpModal.projects.planningList.targetPopulation')}</Text></li>
                        <li><Text>{t('component:helpModal.projects.planningList.indicators')}</Text></li>
                    </ul>
                </div>
                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.projects.budgetManagement')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.projects.budgetList.detailedBudgets')}</Text></li>
                        <li><Text>{t('component:helpModal.projects.budgetList.realTimeControl')}</Text></li>
                        <li><Text>{t('component:helpModal.projects.budgetList.multipleFunding')}</Text></li>
                        <li><Text>{t('component:helpModal.projects.budgetList.autoJustifications')}</Text></li>
                        <li><Text>{t('component:helpModal.projects.budgetList.liquidityForecasts')}</Text></li>
                    </ul>
                </div>
                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.projects.monitoring')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.projects.monitoringList.activities')}</Text></li>
                        <li><Text>{t('component:helpModal.projects.monitoringList.indicators')}</Text></li>
                        <li><Text>{t('component:helpModal.projects.monitoringList.impact')}</Text></li>
                        <li><Text>{t('component:helpModal.projects.monitoringList.reports')}</Text></li>
                    </ul>
                </div>
                <div style={{ marginLeft: 32, marginTop: 12 }}>
                    <Tag color="red">{t('component:helpModal.projects.tags.strategicPlanning')}</Tag>
                    <Tag color="volcano">{t('component:helpModal.projects.tags.complexBudgets')}</Tag>
                    <Tag color="gold">{t('component:helpModal.projects.tags.continuousMonitoring')}</Tag>
                    <Tag color="lime">{t('component:helpModal.projects.tags.integratedResources')}</Tag>
                    <Tag color="cyan">{t('component:helpModal.projects.tags.impactEvaluation')}</Tag>
                </div>
            </div>

            {/* Beneficiarios */}
            <div style={{ marginBottom: 24 }}>
                <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col>
                        <SolutionOutlined style={{ fontSize: '20px', color: '#006778' }} />
                    </Col>
                    <Col>
                        <Title level={5} style={{ margin: 0 }}>{t('component:helpModal.beneficiaries.title')}</Title>
                    </Col>
                </Row>
                <Paragraph style={{ marginLeft: 32, marginBottom: 8 }}>
                    {t('component:helpModal.beneficiaries.description')}
                </Paragraph>

                <div style={{ marginLeft: 48, marginTop: 16 }}>
                    <Text strong>{t('component:helpModal.beneficiaries.personalData')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.beneficiaries.personalDataList.identification')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.personalDataList.contact')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.personalDataList.family')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.personalDataList.nationality')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.personalDataList.photo')}</Text></li>
                    </ul>
                </div>

                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.beneficiaries.socioeconomic')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.beneficiaries.socioeconomicList.income')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.socioeconomicList.expenses')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.socioeconomicList.thresholds')}</Text></li>
                    </ul>
                </div>

                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.beneficiaries.interventions')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.beneficiaries.interventionsList.chronological')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.interventionsList.followUp')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.interventionsList.individualized')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.interventionsList.evaluation')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.interventionsList.coordination')}</Text></li>
                    </ul>
                </div>

                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.beneficiaries.documentation')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.beneficiaries.documentationList.digitalization')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.documentationList.autoGeneration')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.documentationList.expiration')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.documentationList.signature')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.documentationList.gdpr')}</Text></li>
                    </ul>
                </div>

                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.beneficiaries.health')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.beneficiaries.healthList.disabilities')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.healthList.medication')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.healthList.dependency')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.healthList.coordination')}</Text></li>
                    </ul>
                </div>

                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.beneficiaries.itineraries')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.beneficiaries.itinerariesList.individualized')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.itinerariesList.objectives')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.itinerariesList.compliance')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.itinerariesList.results')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.itinerariesList.adaptation')}</Text></li>
                    </ul>
                </div>

                <div style={{ marginLeft: 48, marginTop: 12 }}>
                    <Text strong>{t('component:helpModal.beneficiaries.derivations')}</Text>
                    <ul style={{ marginTop: 8 }}>
                        <li><Text>{t('component:helpModal.beneficiaries.derivationsList.specialized')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.derivationsList.followUp')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.derivationsList.reports')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.derivationsList.interdisciplinary')}</Text></li>
                        <li><Text>{t('component:helpModal.beneficiaries.derivationsList.network')}</Text></li>
                    </ul>
                </div>

                <div style={{ marginLeft: 32, marginTop: 16 }}>
                    <Tag color="geekblue">{t('component:helpModal.beneficiaries.tags.digitalFile')}</Tag>
                    <Tag color="purple">{t('component:helpModal.beneficiaries.tags.economicTracking')}</Tag>
                    <Tag color="cyan">{t('component:helpModal.beneficiaries.tags.interventions')}</Tag>
                    <Tag color="magenta">{t('component:helpModal.beneficiaries.tags.documentation')}</Tag>
                    <Tag color="orange">{t('component:helpModal.beneficiaries.tags.healthDependency')}</Tag>
                    <Tag color="green">{t('component:helpModal.beneficiaries.tags.itineraries')}</Tag>
                    <Tag color="blue">{t('component:helpModal.beneficiaries.tags.derivations')}</Tag>
                </div>
            </div>

            {/* Catálogo */}
            <div style={{ marginBottom: 24 }}>
                <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                    <Col>
                        <ProfileOutlined style={{ fontSize: '20px', color: '#006778' }} />
                    </Col>
                    <Col>
                        <Title level={5} style={{ margin: 0 }}>{t('component:helpModal.catalogue.title')}</Title>
                    </Col>
                </Row>
                <Paragraph style={{ marginLeft: 32, marginBottom: 8 }}>
                    {t('component:helpModal.catalogue.description')}
                </Paragraph>

                <div style={{ marginLeft: 48, marginTop: 16 }}>
                    <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                        <Col><RiseOutlined style={{ color: '#52c41a' }} /></Col>
                        <Col><Text strong>{t('component:helpModal.catalogue.serviceCatalog.title')}</Text></Col>
                    </Row>
                    <div style={{ marginLeft: 32, marginBottom: 12 }}>
                        <Text>{t('component:helpModal.catalogue.serviceCatalog.description')}</Text>
                        <ul style={{ marginTop: 8 }}>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.items.definition')}</Text></li>
                        </ul>
                    </div>

                    <div style={{ marginLeft: 32, marginBottom: 12 }}>
                        <Text strong>{t('component:helpModal.catalogue.serviceCatalog.serviceTypes')}</Text>
                        <ul style={{ marginTop: 8 }}>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.acc_administrative_procedures')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.social_support')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.lodging')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.psychological')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.search_house')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.social_canteen')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.courses-lectures')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.housing_arragements')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.delivery')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.training')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.socio-educational_training')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.socio-ocuppational_training')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.social_integration')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.events')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.active_job_search')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.couseling')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.others')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.legal_services')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.serviceCatalog.serviceTypesList.workshops')}</Text></li>
                        </ul>
                    </div>

                    <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
                        <Col><SwapOutlined style={{ color: '#52c41a' }} /></Col>
                        <Col><Text strong>{t('component:helpModal.catalogue.derivationSystem.title')}</Text></Col>
                    </Row>
                    <div style={{ marginLeft: 32, marginBottom: 12 }}>
                        <Text>{t('component:helpModal.catalogue.derivationSystem.description')}</Text>
                        <ul style={{ marginTop: 8 }}>
                            <li><Text>{t('component:helpModal.catalogue.derivationSystem.items.directory')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.derivationSystem.items.protocols')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.derivationSystem.items.communication')}</Text></li>
                            <li><Text>{t('component:helpModal.catalogue.derivationSystem.items.followUp')}</Text></li>
                        </ul>
                    </div>
                </div>
                <div style={{ marginLeft: 32, marginTop: 12 }}>
                    <Tag color="processing">{t('component:helpModal.catalogue.tags.socialServices')}</Tag>
                    <Tag color="warning">{t('component:helpModal.catalogue.tags.derivationNetwork')}</Tag>
                    <Tag color="success">{t('component:helpModal.catalogue.tags.collaborativeTracking')}</Tag>
                </div>
            </div>            <Divider orientation="left">
                <Title level={4} style={{ color: '#006778' }}>
                    🎯 {t('component:helpModal.workflowTitle')}
                </Title>
            </Divider>

            <div style={{ backgroundColor: '#f0f8ff', padding: 20, borderRadius: 8, marginBottom: 24 }}>
                <ol style={{ paddingLeft: 20 }}>
                    <li style={{ marginBottom: 12 }}>
                        <Text strong>{t('component:helpModal.workflow.initialConfig.title')}</Text>
                        <ul style={{ marginTop: 8, marginLeft: 16 }}>
                            <li><Text>{t('component:helpModal.workflow.initialConfig.items.basicData')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.initialConfig.items.users')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.initialConfig.items.services')}</Text></li>
                        </ul>
                    </li>
                    <li style={{ marginBottom: 12 }}>
                        <Text strong>{t('component:helpModal.workflow.beneficiariesManagement.title')}</Text>
                        <ul style={{ marginTop: 8, marginLeft: 16 }}>
                            <li><Text>{t('component:helpModal.workflow.beneficiariesManagement.items.completeFiles')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.beneficiariesManagement.items.digitization')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.beneficiariesManagement.items.evaluations')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.beneficiariesManagement.items.itineraries')}</Text></li>
                        </ul>
                    </li>
                    <li style={{ marginBottom: 12 }}>
                        <Text strong>{t('component:helpModal.workflow.projectDesign.title')}</Text>
                        <ul style={{ marginTop: 8, marginLeft: 16 }}>
                            <li><Text>{t('component:helpModal.workflow.projectDesign.items.objectives')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.projectDesign.items.budgets')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.projectDesign.items.resources')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.projectDesign.items.schedule')}</Text></li>
                        </ul>
                    </li>
                    <li style={{ marginBottom: 12 }}>
                        <Text strong>{t('component:helpModal.workflow.execution.title')}</Text>
                        <ul style={{ marginTop: 8, marginLeft: 16 }}>
                            <li><Text>{t('component:helpModal.workflow.execution.items.link')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.execution.items.register')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.execution.items.followUp')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.execution.items.coordinate')}</Text></li>
                        </ul>
                    </li>
                    <li style={{ marginBottom: 12 }}>
                        <Text strong>{t('component:helpModal.workflow.economicControl.title')}</Text>
                        <ul style={{ marginTop: 8, marginLeft: 16 }}>
                            <li><Text>{t('component:helpModal.workflow.economicControl.items.expenses')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.economicControl.items.budget')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.economicControl.items.subsidies')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.economicControl.items.inventory')}</Text></li>
                        </ul>
                    </li>
                    <li style={{ marginBottom: 0 }}>
                        <Text strong>{t('component:helpModal.workflow.evaluation.title')}</Text>
                        <ul style={{ marginTop: 8, marginLeft: 16 }}>
                            <li><Text>{t('component:helpModal.workflow.evaluation.items.indicators')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.evaluation.items.reports')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.evaluation.items.satisfaction')}</Text></li>
                            <li><Text>{t('component:helpModal.workflow.evaluation.items.memories')}</Text></li>
                        </ul>
                    </li>
                </ol>
            </div>

            <Divider orientation="left">
                <Title level={4} style={{ color: '#006778' }}>
                    💡 {t('component:helpModal.tipsTitle')}
                </Title>
            </Divider>

            <div style={{ backgroundColor: '#f6ffed', padding: 16, borderRadius: 8, marginBottom: 24 }}>
                <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                    <li style={{ marginBottom: 8 }}>
                        <Text>{t('component:helpModal.tips.filters')}</Text>
                    </li>
                    <li style={{ marginBottom: 8 }}>
                        <Text>{t('component:helpModal.tips.documentation')}</Text>
                    </li>
                    <li style={{ marginBottom: 8 }}>
                        <Text>{t('component:helpModal.tips.budgets')}</Text>
                    </li>
                    <li style={{ marginBottom: 8 }}>
                        <Text>{t('component:helpModal.tips.derivations')}</Text>
                    </li>
                    <li>
                        <Text>{t('component:helpModal.tips.reports')}</Text>
                    </li>
                </ul>
            </div>

            <div style={{ textAlign: 'center', marginTop: 24 }}>
                <Text type="secondary" style={{ fontSize: '14px' }}>
                    {t('component:helpModal.supportText')}
                </Text>
            </div>
        </Modal>
    );
};
